import { makeAutoObservable, runInAction } from 'mobx';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { deleteEnrollment } from 'api/operations/enrollmentService';
import ModalDelegate from 'store/general/modalDelegate';
import { TriggerRenewalsPeriod } from '../types';

interface RemovePeriodContext {
  period: TriggerRenewalsPeriod;
  groupId: string;
  groupName: string;
}

interface RemovePeriodModalDelegateParams {
  onPeriodRemoved: (periodId: string, groupId: string) => void;
}

export class RemovePeriodModalDelegate {
  private readonly _modalDelegate = new ModalDelegate();
  private readonly _onPeriodRemoved: (periodId: string, groupId: string) => void;
  private _period: TriggerRenewalsPeriod | null = null;
  private _groupId = '';
  private _groupName = '';

  public get isVisible(): boolean {
    return this._modalDelegate.isVisible;
  }

  public get isLoading(): boolean {
    return this._modalDelegate.isLoading;
  }

  public get groupName(): string {
    return this._groupName;
  }

  public get period(): TriggerRenewalsPeriod | null {
    return this._period;
  }

  public open({ period, groupId, groupName }: RemovePeriodContext): void {
    this._period = period;
    this._groupId = groupId;
    this._groupName = groupName;
    this._modalDelegate.open();
  }

  public close(): void {
    this._period = null;
    this._groupId = '';
    this._groupName = '';
    this._modalDelegate.setIsLoading(false);
    this._modalDelegate.close();
  }

  public async confirm(): Promise<void> {
    const period = this._period;
    if (!period) {
      throw new Error('Tried to remove a period without an active context!');
    }

    this._modalDelegate.setIsLoading(true);
    try {
      await deleteEnrollment(period.id);
      runInAction(() => {
        this._onPeriodRemoved(period.id, this._groupId);
        this.close();
      });
      toastManager.addToast('Period was successfully removed', ToastColorTypes.SUCCESS);
    } catch {
      toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
      runInAction(() => {
        this._modalDelegate.setIsLoading(false);
      });
    }
  }

  constructor({ onPeriodRemoved }: RemovePeriodModalDelegateParams) {
    this._onPeriodRemoved = onPeriodRemoved;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
